/**
 * Menu API Service
 *
 * Provides menu configuration for the application. Loads the menu
 * structure from the backend via the generated typed `@api` client
 * (GET /api/v1/admin/menu through `AdminApiService.getAdminMenu()`).
 * When the backend endpoint is unavailable or returns an empty/invalid
 * payload, falls back to a comprehensive static menu configuration
 * derived from the application's route definitions and role requirements.
 *
 * Role-based filtering is NOT performed here -- that responsibility
 * belongs to MenuService's computed signals which check the user's
 * actual permissions via AuthStore. This service only annotates items
 * with `requiredRoles` / `requiredPermissions` metadata.
 *
 * GAP-FE-055 (resolved): renamed from `MenuApiService` → `MenuFeatureApiService`
 * so the selector no longer overlaps with the generated `@api/ApiService` family.
 *
 * GAP-079 (resolved): migrated from the hand-rolled
 * `HttpClient.get('/admin/menu')` to the generated typed
 * `AdminApiService.getAdminMenu()`. The service no longer holds
 * `inject(HttpClient)`; the `@audit GAP-FE-B005` banner and the matching
 * AUDIT_HTTP_DEBT.md row (#23) were removed accordingly.
 *
 * GAP-079 residual (resolved 2026-07-15): the rendered sidebar now ACTUALLY
 * consumes this service. `MenuService` (core/services/menu.service.ts — the
 * authority behind sidebar-modern's `visibleMenu()`) fetches `getMenu()` once
 * an authenticated session exists (re-fetching when the user OR the active
 * tenant changes — the payload is tenant-scoped) and merges the
 * caller-shaped payload onto its vetted static menu by ROUTE: present items
 * adopt the server's `requiredRoles`/`requiredPermissions`, absent items are
 * hidden. Previously the payload was only adopted on an exact topology match
 * that could never hold, so server-driven shaping was inert in the product.
 */
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminApiService } from '@api/api/admin.service';
import {
  MenuSection as ApiMenuSection,
  MenuItem as ApiMenuItem,
} from '@api/model/models';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../services/logger.service';
import { ANOMALY_PERMISSIONS, ROLES } from '@core/constants/permissions';
import type { MenuItem, MenuSection } from '@core/models/menu.model';
import { AUTO_OPTIMIZATION_SURFACE_ENABLED } from '@core/config/deployment-surfaces';

export type { MenuItem, MenuSection } from '@core/models/menu.model';

@Injectable({
  providedIn: 'root'
})
export class MenuFeatureApiService {
  private readonly adminApi = inject(AdminApiService);
  private readonly logger = inject(LoggerService);

  /**
   * Load menu configuration from the backend API.
   *
   * Calls the typed `AdminApiService.getAdminMenu()` (GET /api/v1/admin/menu).
   * On an empty/non-array payload OR any error (404, network, timeout, 5xx)
   * falls back to the static default menu so navigation is never blank. The
   * 404-vs-outage log-level distinction is preserved for monitoring.
   */
  getMenu(): Observable<MenuSection[]> {
    return this.adminApi.getAdminMenu().pipe(
      map((sections) => {
        if (!Array.isArray(sections) || sections.length === 0) {
          this.logger.info(
            'Backend returned empty menu, using client-side defaults',
            { component: 'MenuFeatureApiService' }
          );
          return applyFeatureFlagGating(buildDefaultMenu());
        }
        // Normalize the generated model (`string | null` on optional fields)
        // to the canonical FE model (`string | undefined`) before gating.
        const normalized = sections.map(normalizeSection);
        this.logger.info(
          'Menu loaded from backend API',
          { component: 'MenuFeatureApiService', sectionsCount: normalized.length }
        );
        // GAP-FE-117: gate even backend-served menus so a stale server
        // response cannot expose flag-gated entries to the operator.
        return applyFeatureFlagGating(normalized);
      }),
      catchError((error) => {
        // GAP-025: distinguish the EXPECTED 404 (the `/admin/menu` endpoint may
        // not be served in every environment — static fallback is by design)
        // from a genuine backend OUTAGE (5xx / network). The menu must stay
        // usable for navigation, so we still serve the static default in both
        // cases, but an outage is logged at warn level so it is NOT silently
        // masked as an `info` "endpoint-not-implemented" event in monitoring.
        const status = (error as { status?: number } | null)?.status;
        if (status === 404) {
          this.logger.info(
            'Backend menu API unavailable, using client-side defaults',
            { component: 'MenuFeatureApiService', status }
          );
        } else {
          this.logger.warn(
            'Backend menu API errored (degraded) — serving client-side defaults',
            { component: 'MenuFeatureApiService', status }
          );
        }
        return of(applyFeatureFlagGating(buildDefaultMenu()));
      })
    );
  }
}

/**
 * Normalize a generated `@api` MenuSection into the canonical FE
 * `MenuSection`. The generated model declares the optional string fields
 * as `string | null` (serde omits them at runtime via
 * `skip_serializing_if`, but the TS type still carries `| null`); map
 * `null → undefined` so the result is assignable to the FE model
 * (`icon?: string`, `badge?: string | number`, ...).
 */
function normalizeSection(section: ApiMenuSection): MenuSection {
  return {
    id: section.id,
    title: section.title,
    order: section.order,
    items: section.items.map(normalizeItem),
  };
}

function normalizeItem(item: ApiMenuItem): MenuItem {
  // The generated model is flat (no `children`), but a server response could
  // in principle nest items. Preserve + recurse into `children` when present
  // so the recursive `applyFeatureFlagGating` defense still sees them.
  const raw = item as ApiMenuItem & { children?: ApiMenuItem[] };
  const normalized: MenuItem = {
    id: item.id,
    label: item.label,
    order: item.order,
    icon: item.icon ?? undefined,
    route: item.route ?? undefined,
    badge: item.badge ?? undefined,
    badgeClass: item.badgeClass ?? undefined,
    tooltip: item.tooltip ?? undefined,
    requiredRoles: item.requiredRoles,
    requiredPermissions: item.requiredPermissions,
  };
  if (raw.children) {
    normalized.children = raw.children.map(normalizeItem);
  }
  return normalized;
}

/**
 * GAP-FE-117 · Feature-flag gated menu entries.
 *
 * Sidebar entries whose corresponding backend endpoint is still in 501
 * stub-response mode (Phase 0). The component-level `stubMode` signal
 * remains as defensive fallback for direct-URL navigation, but the
 * sidebar entry is hidden until the backing endpoint is real.
 *
 * Keys correspond to `MenuItem.id`. When the flag is `false`, the item
 * is filtered out of its parent section before the menu is returned.
 *
 * To remove an entry from this map (i.e. always show it), delete the
 * mapping AND remove the `enable*` flag from `FeatureFlags`.
 *
 * GAP-FE-W01 r2 · Exported (was previously file-local) so the
 * menu-drift spec can build an INTENTIONALLY_FEATURE_GATED_ROUTES
 * allowlist from the same source-of-truth (rather than maintaining a
 * parallel string list that drifts). The spec asserts that operator-
 * facing routes are either in the menu, in INTENTIONALLY_PALETTE_ONLY,
 * or in this gated map (with the gate keyed by an existing FeatureFlag).
 */
export const FLAG_GATED_ITEM_IDS: Record<string, keyof typeof environment.features> = {
  'copilot-summaries': 'enableCopilotSummaries',
  'agent-proposals': 'enableAgentProposals',
  // GAP-FE-H003: agent-controls and agent-invariants share the same
  // Phase-1b stub backend (HTTP 501) and must be hidden from the
  // sidebar in prod/staging until the executor data-access layer
  // ships. The in-page fallback remains for direct URL navigation.
  'agent-controls': 'enableAgentControls',
  'agent-invariants': 'enableAgentInvariants',
};

/**
 * Strip menu items whose feature-flag is disabled. Operates on a fresh
 * copy so the static `buildDefaultMenu()` output is never mutated in
 * place (which would persist filtering across getMenu() invocations).
 */
function applyFeatureFlagGating(sections: MenuSection[]): MenuSection[] {
  const flags = environment.features;

  // GAP-FE-W01 r4 (codex round-3): backend-served menus can nest items
  // under `MenuItem.children`. The prior filter only walked top-level
  // `section.items` so a flag-gated entry nested under a parent
  // (e.g., a backend response that groups copilot-summaries under
  // a "Tools" parent) would leak into the sidebar with the flag set
  // to false in prod/staging. Recursively gate at every depth.
  const gateItems = (items: readonly MenuItem[]): MenuItem[] => {
    const out: MenuItem[] = [];
    for (const item of items) {
      const flagKey = FLAG_GATED_ITEM_IDS[item.id];
      if (flagKey && flags[flagKey] !== true) {
        // Item itself is gated off — drop it (and by extension all
        // children, which become unreachable when the parent is
        // hidden).
        continue;
      }
      // Item survives gating; recurse into children if present.
      const filtered: MenuItem = item.children
        ? { ...item, children: gateItems(item.children) }
        : { ...item };
      out.push(filtered);
    }
    return out;
  };

  return sections.map(section => ({
    ...section,
    items: gateItems(section.items),
  }));
}

/**
 * Build the static default menu structure.
 *
 * Every route, role requirement, and ordering matches the definitions in
 * app.routes.ts and the existing MenuService.pagesMenu signal. This is
 * the single source of truth for menu structure when the backend API is
 * unavailable.
 *
 * GAP-FE-W01 r3 · Exported (was previously file-local) so the menu-
 * drift spec can verify that every FLAG_GATED_ITEM_IDS id is actually
 * present in the ungated default menu — preventing a deleted/never-
 * added menu entry from being masked by adding its id to the gating
 * map. With this export the spec asserts both:
 *   (1) Every operator-facing route is in buildDefaultMenu OR
 *       INTENTIONALLY_PALETTE_ONLY OR INTENTIONALLY_FEATURE_GATED
 *   (2) Every FLAG_GATED_ITEM_IDS id exists in buildDefaultMenu()
 *       (i.e., the gate hides a real entry, not a phantom)
 */
export function buildDefaultMenu(): MenuSection[] {
  return [
    {
      id: 'main',
      title: 'Main',
      order: 1,
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'pi pi-home',
          route: '/dashboard',
          order: 1
        }
      ]
    },
    {
      id: 'data-management',
      title: 'Data Management',
      order: 2,
      items: [
        {
          id: 'symbols',
          label: 'Symbols',
          icon: 'pi pi-box',
          route: '/symbols',
          order: 1,
          requiredRoles: [ROLES.SYMBOL_ADMIN, ROLES.SCHEMA_EDITOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'schema-management',
          label: 'Schema Management',
          icon: 'pi pi-database',
          route: '/schema-management',
          order: 2,
          requiredPermissions: ['schema:read'],
          requiredRoles: [ROLES.SCHEMA_EDITOR, ROLES.DBA, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'symbol-mapping',
          label: 'Symbol Mapping',
          icon: 'pi pi-link',
          route: '/admin/symbol-mapping',
          order: 3,
          requiredRoles: [ROLES.SYMBOL_ADMIN, ROLES.SCHEMA_EDITOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'data-quality',
          label: 'Data Quality',
          icon: 'pi pi-shield',
          route: '/data-quality',
          order: 4,
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'exchanges',
          label: 'Exchanges',
          icon: 'pi pi-sync',
          route: '/exchanges',
          order: 5,
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      id: 'trading',
      title: 'Trading',
      order: 3,
      items: [
        {
          id: 'strategy-builder',
          label: 'Strategy Builder',
          icon: 'pi pi-code',
          route: '/strategy-builder',
          order: 1,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'strategies',
          label: 'Strategies',
          icon: 'pi pi-list',
          route: '/strategies',
          order: 2,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'trading-desk',
          label: 'Trading',
          icon: 'pi pi-chart-line',
          route: '/trading',
          order: 3,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'trading-sessions',
          label: 'Trading Sessions',
          icon: 'pi pi-clock',
          route: '/trading-sessions',
          order: 4,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'market-making',
          label: 'Market Making',
          icon: 'pi pi-arrows-h',
          route: '/market-making',
          order: 5,
          tooltip: 'Market-making strategies, inventory, and per-venue spreads',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'paper-trading',
          label: 'Paper Trading',
          icon: 'pi pi-pencil',
          route: '/paper-trading',
          order: 6,
          tooltip: 'Simulated-execution sandbox',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'backtests',
          label: 'Backtests',
          icon: 'pi pi-history',
          route: '/backtests',
          order: 7,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'order-book',
          label: 'Order Book',
          icon: 'pi pi-book',
          route: '/order-book',
          order: 8,
          requiredRoles: [ROLES.TRADER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'feature-sets',
          label: 'Feature Sets',
          icon: 'pi pi-box',
          route: '/features/feature-sets',
          order: 9,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      id: 'risk-management',
      title: 'Risk Management',
      order: 4,
      items: [
        {
          id: 'risk-management',
          label: 'Risk Management',
          icon: 'pi pi-shield',
          route: '/risk-management',
          order: 1,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'risk-controls',
          label: 'Risk Controls',
          icon: 'pi pi-exclamation-triangle',
          route: '/risk-controls',
          order: 2,
          badge: 'CRITICAL',
          badgeClass: 'p-badge-danger',
          tooltip: 'Critical trading safety controls including kill switch',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'agent-controls',
          label: 'Agent Controls',
          icon: 'pi pi-power-off',
          route: '/agent-controls',
          order: 3,
          badge: 'CRITICAL',
          badgeClass: 'p-badge-danger',
          tooltip: 'Tenant-wide agent kill-switch and per-symbol mode',
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'risk-profiles',
          label: 'Risk Profiles',
          icon: 'pi pi-sliders-h',
          route: '/risk-profiles',
          order: 4,
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'auto-de-risking',
          label: 'Auto De-Risking',
          icon: 'pi pi-shield',
          route: '/auto-de-risking',
          order: 5,
          tooltip: 'Automated risk-reduction decisions and triggers',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'pnl-analytics',
          label: 'P&L Analytics',
          icon: 'pi pi-chart-bar',
          route: '/pnl-analytics',
          order: 6,
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'pl-dashboard',
          label: 'P&L Dashboard',
          icon: 'pi pi-dollar',
          route: '/pl-dashboard',
          order: 7,
          tooltip: 'Real-time P&L overview',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'profitability',
          label: 'Net Profitability',
          icon: 'pi pi-chart-line',
          route: '/profitability',
          order: 8,
          tooltip: 'Expected gross-to-net economics and shadow profitability decisions',
          requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      id: 'hft-performance',
      title: 'HFT & Performance',
      order: 5,
      items: [
        {
          id: 'hft-metrics',
          label: 'HFT Live Metrics',
          icon: 'pi pi-chart-bar',
          route: '/hft-metrics',
          order: 1,
          badge: 'LIVE',
          badgeClass: 'p-badge-danger',
          tooltip: 'Real-time high-frequency trading system performance metrics',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR]
        },
        {
          id: 'order-diagnostics',
          label: 'Order Diagnostics',
          icon: 'pi pi-search-plus',
          route: '/order-diagnostics',
          order: 2,
          badge: 'NEW',
          badgeClass: 'p-badge-success',
          tooltip: 'Real-time order tracking and diagnostics with nanosecond precision',
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR, ROLES.TRADER, ROLES.STRATEGY_MANAGER]
        },
        {
          id: 'performance-monitoring',
          label: 'Performance Monitoring',
          icon: 'pi pi-chart-line',
          route: '/performance-monitoring',
          order: 3,
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR]
        },
        {
          id: 'heat-maps',
          label: 'Heat Maps',
          icon: 'pi pi-th-large',
          route: '/heat-maps',
          order: 4,
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR, ROLES.TRADER, ROLES.STRATEGY_MANAGER]
        },
        {
          id: 'telemetry',
          label: 'Telemetry',
          icon: 'pi pi-wave-pulse',
          route: '/telemetry',
          order: 5,
          tooltip: 'Platform telemetry streams and traces',
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      id: 'ml-analytics',
      title: 'ML & Analytics',
      order: 6,
      items: [
        {
          id: 'ml-anomaly-detection',
          label: 'ML Anomaly Detection',
          icon: 'pi pi-bolt',
          route: '/ml-anomaly-detection',
          order: 1,
          // MUST mirror the /ml-anomaly-detection route guard (app.routes.ts)
          // and menu.rs. STRATEGY_MANAGER is rejected by that guard (no
          // anomaly:view in ROLE_DEFAULT_PERMISSIONS and not in its
          // requiredRoles) — listing it here handed them a dead link, while
          // omitting TENANT_ADMIN hid an accessible page from the sidebar.
          // The guard is permissionGuard (roles OR anomaly:view), so the
          // menu gate must carry the SAME permission alternative:
          // tenant-viewer holds anomaly:view by default and could navigate
          // to the page while a roles-only gate hid it from the nav.
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN, ROLES.TENANT_OPERATOR],
          requiredPermissions: [ANOMALY_PERMISSIONS.VIEW]
        },
        {
          id: 'ml-monitoring',
          label: 'ML Monitoring',
          icon: 'pi pi-chart-line',
          route: '/ml-monitoring',
          order: 2,
          tooltip: 'ML model performance and drift monitoring',
          requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'innovation-control',
          label: 'Innovation Control',
          icon: 'pi pi-sparkles',
          route: '/innovation-control',
          order: 3,
          tooltip: 'Governed research, model shadow evidence, capture completeness, allocation enforcement, and durable backtests',
          requiredPermissions: ['allocation:read']
        }
      ]
    },
    {
      id: 'operations',
      title: 'Operations',
      order: 7,
      items: [
        {
          id: 'predictive-maintenance',
          label: 'Predictive Maintenance',
          icon: 'pi pi-wrench',
          route: '/predictive-maintenance',
          order: 1,
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'copilot-summaries',
          label: 'Co-Pilot Summaries',
          icon: 'pi pi-comments',
          route: '/copilot-summaries',
          order: 2,
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'agent-proposals',
          label: 'Agent Proposals',
          icon: 'pi pi-check-square',
          route: '/agent-proposals',
          order: 3,
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'agent-invariants',
          label: 'Agent Invariants',
          icon: 'pi pi-lock',
          route: '/agent-invariants',
          order: 4,
          tooltip: 'Read/edit invariants enforced on agent decisions',
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        ...(AUTO_OPTIMIZATION_SURFACE_ENABLED
          ? [{
              id: 'auto-optimization',
              label: 'Auto Optimization',
              icon: 'pi pi-cog',
              route: '/auto-optimization',
              order: 5,
              tooltip: 'Strategy parameter auto-optimization',
              requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
            }]
          : [])
      ]
    },
    {
      id: 'compliance',
      title: 'Compliance & Reporting',
      order: 8,
      items: [
        {
          id: 'regulatory-reporting',
          label: 'Regulatory Reporting',
          icon: 'pi pi-file',
          route: '/regulatory-reporting',
          order: 1,
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'client-portal',
          label: 'Client Portal',
          icon: 'pi pi-users',
          route: '/client-portal',
          order: 2,
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'client-accounts',
          label: 'Client Accounts',
          icon: 'pi pi-id-card',
          route: '/client-accounts',
          order: 3,
          tooltip: 'Client account management and KYC artefacts',
          requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'audit-logs',
          label: 'Audit Logs',
          icon: 'pi pi-eye',
          route: '/admin/audit',
          order: 4,
          requiredRoles: [ROLES.TENANT_VIEWER, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'reports',
          label: 'Reports',
          icon: 'pi pi-file-edit',
          route: '/reports',
          order: 5
        }
      ]
    },
    {
      id: 'configuration',
      title: 'Configuration',
      order: 9,
      items: [
        {
          id: 'environment-config',
          label: 'Environment Config',
          icon: 'pi pi-cog',
          route: '/config',
          order: 1,
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'config-management',
          label: 'Config Management',
          icon: 'pi pi-sliders-h',
          route: '/config-management',
          order: 2,
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'multi-tenant',
          label: 'Multi-Tenant',
          icon: 'pi pi-building',
          route: '/multi-tenant',
          order: 3,
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        },
      ]
    },
    {
      id: 'developer',
      title: 'Developer Tools',
      order: 10,
      items: [
        {
          id: 'api-management',
          label: 'API Management',
          icon: 'pi pi-code',
          route: '/api-management',
          order: 1,
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      id: 'administration',
      title: 'Administration',
      order: 11,
      items: [
        {
          id: 'users',
          label: 'Users',
          icon: 'pi pi-users',
          route: '/users',
          order: 1,
          requiredPermissions: ['users:read'],
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'tenants',
          label: 'Tenants',
          icon: 'pi pi-building',
          route: '/tenants',
          order: 2,
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        },
        {
          id: 'permissions',
          label: 'Permissions',
          icon: 'pi pi-key',
          route: '/permissions-management',
          order: 3,
          // Roles-only, mirroring the /permissions-management route guard
          // (roleGuard with [TENANT_ADMIN, SYSTEM_ADMIN], no permission
          // alternative) and menu.rs. The former ['role:read', 'user:read']
          // permission gate would (OR-semantics) surface the entry to
          // tenant-operator/tenant-viewer, whom the guard rejects.
          requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
          tooltip: 'Manage roles, permissions, and access control'
        },
        {
          id: 'admin-control-panel',
          label: 'Admin Control Panel',
          icon: 'pi pi-th-large',
          route: '/admin/control-panel',
          order: 4,
          tooltip: 'System-admin control surface (feature flags, ops switches)',
          requiredRoles: [ROLES.SYSTEM_ADMIN]
        }
      ]
    },
    {
      id: 'monitoring',
      title: 'Monitoring',
      order: 12,
      items: [
        {
          id: 'system-health',
          label: 'System Health',
          icon: 'pi pi-heart',
          route: '/system-health',
          order: 1,
          requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR]
        }
      ]
    },
    {
      id: 'settings-group',
      title: 'System',
      order: 13,
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: 'pi pi-cog',
          route: '/settings',
          order: 1
        },
        {
          id: 'help',
          label: 'Help',
          icon: 'pi pi-question-circle',
          route: '/help',
          order: 2
        }
      ]
    }
  ];
}
